import { type ReactNode, useState } from 'react';
import { CaretRightIcon, CaretDownIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Badge } from '../Badge';

type CollapsibleSectionProps = {
    label: string;
    count?: number;
    countAnimated?: boolean;
    muted?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    action?: ReactNode;
    children: ReactNode;
    className?: string;
};

export function CollapsibleSection({
    label,
    count,
    countAnimated = true,
    muted = false,
    defaultOpen = true,
    onOpenChange,
    action,
    children,
    className,
}: CollapsibleSectionProps) {
    const [open, setOpenState] = useState(defaultOpen);

    const setOpen = (next: boolean) => {
        setOpenState(next);
        onOpenChange?.(next);
    };

    return (
        <div
            className={cn('rounded-[5px] p-4', className)}
            data-state={open ? 'open' : 'closed'}
        >
            <div className="flex flex-row items-center gap-2 w-full min-h-5">
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="flex flex-row items-center gap-2 flex-1 min-w-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-grey-400 focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900 rounded"
                >
                    <span className="flex shrink-0 w-3 h-3 items-center justify-center text-white-100">
                        {open ? (
                            <CaretDownIcon size={12} weight="bold" />
                        ) : (
                            <CaretRightIcon size={12} weight="bold" />
                        )}
                    </span>
                    <span className="flex flex-1 min-w-0 items-center gap-1.5">
                        {muted ? (
                            <span className="text-sm text-grey-200 truncate">
                                {label}
                            </span>
                        ) : (
                            <>
                                <span className="text-sm-medium text-white-100 truncate">
                                    {label}
                                </span>
                                {count !== undefined && (
                                    <Badge
                                        variant="number"
                                        animated={countAnimated}
                                        value={count}
                                    />
                                )}
                            </>
                        )}
                    </span>
                </button>
                {action && (
                    <span className="flex shrink-0 h-8 items-center">
                        {action}
                    </span>
                )}
            </div>
            {open && <div className="mt-2">{children}</div>}
        </div>
    );
}
